import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DirTeamType } from '@econtractor/types';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import CreatableNameSelect from 'components/forms/CreatableNameSelect';
import InputLabel from 'components/others/InputLabel';
import * as apiSettings from 'api/apiSettings';
import * as apiWarehouses from 'api/apiWarehouses';
import { isTeamActive } from 'utils/helpers/teams/isTeamActive';
import type { UserTabProps } from '../../types';

type CreateTeamResponse = {
    stat?: boolean;
    teamId?: number;
    teamName?: string;
    errCode?: string;
} | null;

const UserTeamCreatableSelect = ({ user, dispatchUser }: UserTabProps) => {
    const { t } = useTranslation(['common', 'Warehouses']);
    const { localSettings, dispatchLocalSettings } = useSettings();
    const { showToast } = useToast();
    const value = Number(user?.access?.team) || null;

    const options = useMemo(() => {
        const items = (localSettings?.DIRS?.Team || []) as DirTeamType[];
        return items
            .filter((team) => isTeamActive(team) || team.ID === value)
            .map((team) => ({ value: team.ID, label: team.teamName }));
    }, [localSettings?.DIRS?.Team, value]);

    const selectTeam = (teamId: number) => {
        dispatchUser({
            type: 'SET_ACCESS_FIELD',
            field: 'team',
            value: teamId || 0,
        });
    };

    const handleCreate = async (inputValue: string): Promise<boolean> => {
        const name = String(inputValue || '').trim();
        if (!name) return false;

        const res = (await apiWarehouses.createTeam(name)) as CreateTeamResponse;
        if (res?.stat && res.teamId) {
            const settings = await apiSettings.getSettings();
            if (settings) dispatchLocalSettings({ type: 'SET', settings });
            selectTeam(res.teamId);
            return true;
        }
        if (res?.errCode === 'DUPLICATE_NAME') {
            const existing = options.find((opt) => opt.label.trim().toLowerCase() === name.toLowerCase());
            if (existing) {
                selectTeam(existing.value);
                return true;
            }
            showToast(t('Warehouses:createTeam.messDuplicate'), 'danger');
            return false;
        }
        if (res?.errCode === 'EMPTY_NAME') {
            showToast(t('Warehouses:createTeam.messEmpty'), 'danger');
        } else {
            showToast(t('common:messError'), 'danger');
        }
        return false;
    };

    return (
        <InputLabel title={t('team')}>
            <CreatableNameSelect
                options={options}
                value={value}
                isClearable={false}
                placeholder={t('team')}
                addLabel={t('Users.btnAdd')}
                requiredMessage={t('Warehouses:createTeam.messEmpty')}
                formatCreateLabel={(input) => t('Users.createOption', { name: input })}
                onChange={(option) => selectTeam(option?.value || 0)}
                onCreate={handleCreate}
            />
        </InputLabel>
    );
};

export default UserTeamCreatableSelect;
